import $ from 'jquery';

import ApiBase from "@core/Api/api-base";
import {fetchRemote, fetchRemoteResult} from '@core/Api/api-util';
import securityApi from '@core/Util/security';

const controllerEndpoint = '/JourneyBuilder/';

const validatePermission = (permissionGrantType) => {
    return securityApi.validatePermission(
        securityApi.Permissions.JourneyBuilder,
        permissionGrantType
    );
};
const serializeJourneyPublishModel = (journey, brickComments) => {
    const journeyClone = JSON.parse(JSON.stringify(journey));
    journeyClone.config = JSON.stringify(journey.config);
    
    journeyClone.brickComments = Object.keys(brickComments).map(brickId => ({
        brickId: brickId,
        content: brickComments[brickId]
    }));
    
    journeyClone.bricks.forEach(brick => {
        brick.config = JSON.stringify(brick.config);
        brick.connections.forEach(connection => { connection.config = JSON.stringify(connection.config); });
    });
    
    return JSON.stringify(journeyClone);
};

let _touchpointPromise;
let _audiencePromise;
let _allAudiencePromise;
let _conditionsPromise;
let _deduplicationFieldsPromise;
let _worldsPromise;
let _typesPromise;
let _delayColumns;
let _audienceAttributesPromise;

const fieldsCache = {};

/**
 * Wrap the given async call with a cache layer, executing the call only once per page load
 * @param {function(): Promise<*>} handler Async request handler
 * @return {function(): Promise<*>} Cached async request handler
 */
function wrapWithCache(handler){
    const _cache = {};
    
    return async (...parameters) => {
        //Create a key based on the provided parameters
        const cacheKey = parameters.join('|');
        
        if(_cache[cacheKey]){
            try {
                return await _cache[cacheKey];
            } catch(_){
                //Swallow; an error occurred, retry our API call
            }
        }
        
        _cache[cacheKey] = handler(...parameters);
        return await _cache[cacheKey];
    }
}

export default {
    /**
     * Generate a URL for viewing the given journey
     * @param {String} journeyId
     * @return {string}
     */
    getViewJourneyUrl(journeyId) {
        return `${controllerEndpoint}Index/${journeyId || ''}`;
    },
    getViewJourneyBrickUrl(brickId){
        return `${controllerEndpoint}ViewJourneyBrick/${brickId}`;
    },
    getStatisticsPageUrl(journeyId, highlightedBrickId){
        let endpoint = `${controllerEndpoint}JourneyReport/`;
        
        if(journeyId){
            endpoint += journeyId;
        }
        
        if(highlightedBrickId){
            endpoint += `?highlightedBrickId=${highlightedBrickId}`;
        }
        
        return endpoint;
    },
    /**
     * @param {Number} [periodId]
     * @return {Promise<{start: String, end: String}>}
     */
    getStatisticsPeriodDateRange(periodId){
        const params = {};
        if(periodId !== null && periodId !== undefined){
            params.periodId = periodId;
        }
        
        const queryString = ApiBase.parameterize(params);
        
        return fetchRemoteResult(`${controllerEndpoint}/GetStatisticsPeriodDateRange?${queryString}`);
    },
    /**
     * @param journeyId
     * @param brickId
     * @return {Promise<SankeyTouchpointInfoConfig>}
     */
    getBrickStatisticsConfiguration(journeyId, brickId){
        return fetchRemoteResult(`${controllerEndpoint}/GetBrickStatisticsConfiguration?journeyId=${journeyId}&brickId=${brickId}`);
    },
    /**
     * @param {String} journeyId
     * @param {String} brickId
     * @param {Number} widgetId
     * @param {JourneyReportStatisticsParameters} parameters
     * @returns {Promise<*>}
     */
    getReportStatisticsWidgetData(journeyId, brickId, widgetId, parameters){
        const queryString = ApiBase.parameterize({
            journeyId,
            brickId,
            widgetId,
            ...parameters
        });

        return fetchRemoteResult(`${controllerEndpoint}/GetReportStatisticsWidgetData?${queryString}`);
    },
    /**
     * @param {String} journeyId
     * @param {String} brickId
     * @param {Number} tabId
     * @param {JourneyReportStatisticsParameters} parameters
     * @returns {Promise<{ report: Blob, filename: filename }>}
     */
    async exportReportStatisticsTab(journeyId, brickId, tabId, parameters){
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const queryString = ApiBase.parameterize({
            journeyId,
            brickId,
            tabId,
            ...parameters
        });
        
        const response = await fetchRemote(`${controllerEndpoint}/ExportReportStatisticsTab?${queryString}`);

        //Having our contentType set to application/json implies an error result has been returned
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')){
            const result = await response.json();
            throw result.errors;
        }
        
        //We assume we always have a filename present in our headers
        //https://stackoverflow.com/a/60075318
        const headerFilename = response.headers.get('content-disposition')
            .split(';')
            .map(part => part.trimStart())
            .find(part => part.startsWith('filename'));
        
        //Take relevant part, replace illegal characters
        const filename = headerFilename.split('=')[1].replace(/"/g, '');
        
        const report = await response.blob();
        
        return { report: report, filename: filename };
    },
    async getJourneyById(journeyId) {
        const journey = await fetchRemoteResult(`${controllerEndpoint}GetJourneyById/${journeyId}`);
        journey.config = JSON.parse(journey.config);
        return journey;
    },
    /**
     * @deprecated Use getJourneys instead
     */
    listJourneys(){
        return fetchRemoteResult(`${controllerEndpoint}ListJourneys`);
    },
    deleteJourneyById(journeyId) {
        return fetchRemoteResult(`${controllerEndpoint}DeleteJourney/${journeyId}`);
    },
    getJourneyBrickDefinitions(){
        return fetchRemoteResult(`${controllerEndpoint}GetJourneyBrickDefinitions`);
    },
    getJourneyConditions(){
        return fetchRemoteResult(`${controllerEndpoint}GetJourneyConditions`);
    },
    publishJourney(journey, brickComments){
        const opts = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: serializeJourneyPublishModel(journey, brickComments)
        };
        
        return fetchRemoteResult(`${controllerEndpoint}Publish`, opts);
    },
    cloneJourney(journeyId){
        return fetchRemoteResult(`${controllerEndpoint}Clone/${journeyId}`, {
            method: 'POST'
        });
    },
    getBrickComments(brickId){
        return fetchRemoteResult(`${controllerEndpoint}BrickComments?brickId=${brickId}`);
    },
    getBrickCommentIndicators(journeyId){
        return fetchRemoteResult(`${controllerEndpoint}BrickCommentIndicators?journeyId=${journeyId}`);
    },

    /**
     * Fetches all comments for the related entity of passed type
     * @param {guid} relatedId 
     * @param {"Brick" | "Connection"} type 
     */
    getComments(relatedId, type){
        return fetchRemoteResult(`${controllerEndpoint}GetComments?relatedId=${relatedId}&type=${type}`);
    },
    /**
     * Fetches all indicators for the related journey
     * @param {guid} journeyId 
     */
    getIndicators(journeyId) {
        return fetchRemoteResult(`${controllerEndpoint}GetIndicators?journeyId=${relatedId}`);
    },

    /**
     * Update the given comment with new content
     * @param {{ id: Number, content: String }} parameters
     */
    updateComment(parameters){
        return fetchRemoteResult(`${controllerEndpoint}comment?${ApiBase.parameterize(parameters)}`, {
            method: 'PUT'
        });
    },
    getStatisticsPeriods: wrapWithCache(
        () => fetchRemoteResult(`${controllerEndpoint}GetStatisticsPeriods`)
    ),
    getStatisticsInflowPeriods: wrapWithCache(
        () => fetchRemoteResult(`${controllerEndpoint}GetStatisticsInflowPeriods`)
    ),
    getProducts: wrapWithCache(
        () => fetchRemoteResult(`${controllerEndpoint}GetProducts`)
    ),
    getBrickTrafficGeneralStatistics(destinationBrickId){
        return fetchRemoteResult(
            `${controllerEndpoint}GetBrickTrafficGeneralStatistics?brickId=${destinationBrickId}`
        );
    },
    getTouchpointTrafficStatistics(connectionId, filters){
        const queryString = '?' + $.param({
            touchpointId: connectionId,
            filters: JSON.stringify(filters)
        });
        
        return fetchRemoteResult(
            `${controllerEndpoint}GetTouchpointTrafficStatistics${queryString}`
        );
    },
    getTouchpointTypes(){
        return _touchpointPromise ? _touchpointPromise : (
            _touchpointPromise = fetchRemoteResult(`${controllerEndpoint}GetTouchPointTypes`)
        );
    },
    getTouchpointDeduplicationFields(){
        return _deduplicationFieldsPromise ? _deduplicationFieldsPromise : (
            _deduplicationFieldsPromise = fetchRemoteResult(`${controllerEndpoint}GetTouchpointDeduplicationFields`)
        );
    },
    getTouchPointParentTypes() {
        _touchpointPromise = _touchpointPromise || fetchRemoteResult(`${controllerEndpoint}GetTouchPointTypes`);

        return new Promise((resolve, reject) => {
            _touchpointPromise.then((types) => {
                const parentTypes = [];
    
                //Parent types will be all distinct touchpoint parentTypes
                types.map((type) => {
                    if(type.parentType && parentTypes.indexOf(type.parentType) === -1) {
                        parentTypes.push(type.parentType);
                    }
                });
                resolve(parentTypes);
            }, reject);
        });
    },
    getCustomConditionTypes(){
        return _conditionsPromise || (
            _conditionsPromise = fetchRemoteResult(`${controllerEndpoint}GetCustomConditionTypes`)
        );
    },
    /**
     * @return {Promise<JourneyBuilder.AudienceFilterFieldType[]>}
     */
    getAudienceFilterFieldTypes: wrapWithCache(
      () => fetchRemoteResult(`${controllerEndpoint}GetAudienceFilterFieldTypes`)
    ),
    /**
     * @return {Promise<{ id: String, name: String }[]>}
     */
    getAudiences() {
        return _audiencePromise || (
            _audiencePromise = fetchRemoteResult(`${controllerEndpoint}GetAudiences`)
        );
    },
    getAllAudiences() {
        return _allAudiencePromise || (
            _allAudiencePromise = fetchRemoteResult(`${controllerEndpoint}GetAllAudiences`)
        );
    },
    /**
     * Obtain a list of all journeys in a nested model
     * @return {Promise<JourneyModelBase[]>}
     */
    getJourneys() {
        return fetchRemoteResult(`${controllerEndpoint}ListJourneys`);
    },
    getTouchPointStatistics(touchpointId) {
        return fetchRemoteResult(`${controllerEndpoint}GetTouchPointStatistics?touchPointId=${touchpointId}`);
    },
    /**
     * @returns {Promise<{name: String, id: String, touchPointParentType: String}[]>}
     */
    getGoalTypes: wrapWithCache(() => fetchRemoteResult(`${controllerEndpoint}GetGoalTypes`)),
    /**
     * @returns {Promise<{name: String, id: String}[]>}
     */
    getEmailCampaigns: wrapWithCache(() => fetchRemoteResult(`${controllerEndpoint}GetEmailCampaigns`)),
    /**
     * @returns {Promise<{name: String, id: Number}[]>}
     */
    getSteamCampaigns: wrapWithCache(() => fetchRemoteResult(`${controllerEndpoint}GetSteamCampaigns`)),
    /**
     * @returns {Promise<{name: String, id: String}[]>}
     */
    getDmCampaigns: wrapWithCache(() => fetchRemoteResult(`${controllerEndpoint}GetDmCampaigns`)),
    /**
     * @returns {Promise<{label: String, id: String}>}
     */
    getProductCheckOptions: wrapWithCache(() => fetchRemoteResult(`${controllerEndpoint}GetProductCheckOptions`)),

    /**
     * Get the amount of records affected by deactivation of the given object
     * @param {"journey"|"connection"} objectType
     * @param {String|Number} objectId
     * @returns {Promise<{ total: Number }>}
     */
    getObjectDeactivationAffectedRecordCount(objectType, objectId){
        return fetchRemoteResult(`${controllerEndpoint}GetObjectDeactivationAffectedRecordCount?objectType=${objectType}&objectId=${objectId}`);
    },
    
    getWorlds() {
        return _worldsPromise || (_worldsPromise = fetchRemoteResult(`${controllerEndpoint}GetWorlds`));
    },
    getTypes() {
        return _typesPromise || (_typesPromise = fetchRemoteResult(`${controllerEndpoint}GetTypes`));
    },
    getTouchpointDelayDateColumns() {
        return _delayColumns || (_delayColumns = fetchRemoteResult(`${controllerEndpoint}GetTouchpointDelayDateColumns`));
    },
    getAudienceAttributes(){
        return (async () => {
            if(_audienceAttributesPromise){
                try {
                    return await _audienceAttributesPromise;
                } catch(e) {
                    //Suppress - retry
                }
            }

            _audienceAttributesPromise = fetchRemoteResult(`${controllerEndpoint}GetAudienceAttributes`);
            return _audienceAttributesPromise;
        })();
    },

    /**
     * @return {Promise<JourneyBuilder.CustomField[]>}
     */
    getCustomGoalFields: wrapWithCache(() => fetchRemoteResult(`${controllerEndpoint}GetCustomGoalFields`)),
    
    /**
     * @param {String} touchPointParentType
     * @return {Promise<{ name: String, types: JourneyBuilder.CustomField[] }>}
     */
    getCustomFields(touchPointParentType) {
        //Lazy loading of custom fields per touchpoint parent type.
        const cached = fieldsCache[touchPointParentType];
        if(cached){
            return cached;
        }
        
        return fieldsCache[touchPointParentType] = new Promise((resolve, reject) => {
            fetchRemoteResult(`${controllerEndpoint}GetCustomFields?touchPointParentType=${touchPointParentType}`)
                .then((fields) => {
                    resolve({
                        name: touchPointParentType,
                        types: fields
                    });
                }, function(){
                    //Error; remove our failed promise from the cache 
                    delete fieldsCache[touchPointParentType];
                    reject.apply(this, arguments);
                });
        });
    },
    validateWritePermission() {
        return validatePermission(securityApi.PermissionGrantTypes.Write);
    },
    validateDeletePermission() {
        return validatePermission(securityApi.PermissionGrantTypes.Delete);
    },
    validateActivationPermission() {
        return securityApi.validatePermission(
            securityApi.Permissions.ActivateJourneys,
            securityApi.PermissionGrantTypes.Write
        );
    }
};

export {
    controllerEndpoint      
};